import { REGEX } from '@repo/lib/constants';
import { assert } from './assert.js';

export const DEFAULT_REDACTION = '********';

type Redaction = {
  name: string;
  pattern: RegExp;
  replacement: string;
};

/**
 * Redacts PII or sensitive information in a string based on specified patterns
 * @param str The string to process
 * @param patterns Optional additional redaction patterns
 * @returns String with sensitive information redacted
 */
export const redactSensitiveInfo = (
  str: string,
  patterns: Redaction[] = [],
): string => {
  // Default pattern for email addresses
  const defaultPatterns: Redaction[] = [
    {
      name: 'email',
      pattern: REGEX.EMAIL_GLOBAL,
      replacement: DEFAULT_REDACTION,
    },
    {
      name: 'token',
      pattern: REGEX.JWT_GLOBAL,
      replacement: DEFAULT_REDACTION,
    },
    {
      name: 'zipCode',
      pattern: REGEX.ZIP_CODE_GLOBAL,
      replacement: DEFAULT_REDACTION,
    },
  ];

  // Combine custom and default patterns
  // We want to apply custom patterns first to ensure they take precedence
  const allPatterns = [...patterns, ...defaultPatterns];

  // Apply each pattern
  let result = str;
  allPatterns.forEach(({ pattern, replacement }) => {
    result = result.replace(pattern, replacement);
  });

  return result;
};

/**
 * Extracts the first character from a string, handling Unicode correctly.
 * Uses spread operator to properly handle surrogate pairs (emoji, some CJK).
 */
const firstChar = (str: string): string => [...str][0] ?? '';

/**
 * Derives initials from a name string.
 * - "John Doe" → "JD"
 * - "John" → "J"
 * - "John Michael Doe" → "JD" (first + last)
 *
 * Unicode-safe: handles emoji and non-BMP characters correctly.
 */
export const getInitials = (name: string): string => {
  const parts = name.trim().split(/\s+/);
  const first = parts[0];
  if (!first) return '';

  // After trim + split, parts[0] and parts[length-1] are guaranteed non-empty
  const firstInitial = firstChar(first).toUpperCase();
  const lastInitial =
    parts.length > 1 ? firstChar(parts[parts.length - 1]!).toUpperCase() : '';

  return firstInitial + lastInitial;
};

export function colorizeYaml(
  yaml: string,
  colorize: {
    key: (text: string) => string;
    value: (text: string) => string;
    multilineContent: (text: string) => string;
    multilineIndicator: (text: string) => string;
    comment: (text: string) => string;
    listItem: (text: string) => string;
  },
): string {
  const lines = yaml.split('\n');
  const result: string[] = [];
  let inMultilineString = false;
  let multilineIndent = 0;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    assert(line !== undefined);

    const currentIndent = (() => {
      const match = line.match(/^(\s*)/);
      assert(match !== null);
      const leadingSpaces = match[1];
      assert(leadingSpaces !== undefined);
      return leadingSpaces.length;
    })();

    // Check if we're continuing a multiline string
    if (inMultilineString) {
      // Multiline content continues if indented more than the key
      if (currentIndent > multilineIndent && line.trim() !== '') {
        result.push(colorize.multilineContent(line));
        continue;
      } else {
        // Multiline ended
        inMultilineString = false;
      }
    }

    // Match lines like "  key: value" or "  key: |-"
    const match = line.match(/^(\s*)([^:]+):\s*(.*)$/);
    if (match) {
      const [, indent = '', key = '', value = ''] = match;

      // Check if this starts a multiline string (|-, |, >, >-)
      if (value.match(/^[|>][-+]?$/)) {
        inMultilineString = true;
        multilineIndent = currentIndent;
        result.push(
          indent +
            colorize.key(key) +
            ': ' +
            colorize.multilineIndicator(value),
        );
        continue;
      }

      result.push(indent + colorize.key(key) + ': ' + colorize.value(value));
      continue;
    }

    // Comments or empty lines
    if (line.trim().startsWith('#') || line.trim() === '') {
      result.push(colorize.comment(line));
      continue;
    }

    // List items or other YAML constructs
    if (line.trim().startsWith('-')) {
      result.push(colorize.listItem(line));
      continue;
    }

    // Default: return unmodified
    result.push(line);
  }

  return result.join('\n');
}

/**
 * Converts a string to title case
 * @param value The string to convert
 * @returns The string in title case
 * @example
 * toTitleCase('Late delivery')
 * // => 'Late Delivery'
 */
export const toTitleCase = (value: string) =>
  value
    .toLowerCase()
    .split(' ')
    .map(
      (word) =>
        `${firstChar(word).toUpperCase()}${[...word].slice(1).join('')}`,
    )
    .join(' ');

/**
 * Converts a string to sentence case (first character uppercased).
 * Unicode-safe: handles emoji and non-BMP characters correctly.
 * @param value The string to convert
 * @returns The string in sentence case
 * @example
 * toSentenceCase('email contains @example.com')
 * // => 'Email contains @example.com'
 */
export const toSentenceCase = (value: string): string => {
  if (!value) return value;
  const first = firstChar(value);
  return first.toUpperCase() + [...value].slice(1).join('');
};

/**
 * Converts a snake_case field name to a human-readable sentence-case label.
 * Replaces underscores with spaces and capitalizes the first character.
 * @param name The snake_case field name
 * @returns Human-readable label
 * @example
 * humanizeFieldName('order_items')
 * // => 'Order items'
 * humanizeFieldName('product_sku')
 * // => 'Product sku'
 */
export const humanizeFieldName = (name: string): string =>
  toSentenceCase(name.replace(/_/g, ' '));
